/**
 * MathLive 전용 매크로 → 표준 LaTeX. `mathfield.getValue('latex')` 는 자체 매크로를
 * 뱉고(`dx` 타이핑 → inline shortcut → `\differentialD x`), Temml 에는 없는 이름이라
 * 그대로 넘기면 수식 전체가 `Unsupported function name` 으로 깨진다. 값은 MathLive 의
 * 매크로 정의를 그대로 옮겨 입력기 화면과 렌더 결과를 맞춘다.
 */
const MATHLIVE_MACROS: Readonly<Record<string, string>> = {
  '\\differentialD': '\\mathrm{d}',
  '\\capitalDifferentialD': '\\mathrm{D}',
  '\\exponentialE': '\\mathrm{e}',
  '\\imaginaryI': '\\mathrm{i}',
  '\\imaginaryJ': '\\mathrm{j}',
  '\\rd': '\\mathrm{d}',
  '\\rD': '\\mathrm{D}',
  '\\doubleStruckCapitalN': '\\mathbb{N}',
  '\\doubleStruckCapitalR': '\\mathbb{R}',
  '\\doubleStruckCapitalQ': '\\mathbb{Q}',
  '\\doubleStruckCapitalZ': '\\mathbb{Z}',
  '\\doubleStruckCapitalP': '\\mathbb{P}',
  '\\scriptCapitalE': '\\mathscr{E}',
  '\\scriptCapitalH': '\\mathscr{H}',
  '\\scriptCapitalL': '\\mathscr{L}',
  '\\gothicCapitalC': '\\mathfrak{C}',
  '\\gothicCapitalH': '\\mathfrak{H}',
  '\\gothicCapitalI': '\\mathfrak{I}',
  '\\gothicCapitalR': '\\mathfrak{R}',
  // 자동 fence 변형(`\sin(x)` 등)
  '\\mleft': '\\left',
  '\\mright': '\\right',
  '\\placeholder': '\\square',
};

// 백슬래시 런을 함께 캡처해 홀짝으로 제어 시퀀스와 행 구분자 `\\` 뒤 글자를 가른다.
// 긴 이름 우선 + 뒤따르는 알파벳 배제로 `\rd` 가 `\rdots` 를 삼키지 않게 한다.
const MATHLIVE_MACRO_PATTERN = new RegExp(
  `(\\\\+)(${Object.keys(MATHLIVE_MACROS)
    .map(command => command.slice(1))
    .sort((a, b) => b.length - a.length)
    .join('|')})(?![a-zA-Z])`,
  'g',
);

// 매크로를 정의하거나 문자 그대로 출력하는 구문. 하나라도 있으면 손대지 않는다.
const LITERAL_CONTEXT_PATTERN =
  /\\(?:[gex]?def|let|newcommand|renewcommand|providecommand|verb)(?![a-zA-Z])/;

/**
 * Temml `macros` 옵션이 아니라 입력 문자열을 고치는 이유:
 *
 * 1. `annotate: true` 의 `<annotation>` 은 넘긴 문자열 그대로다. 워크시트 PDF
 *    (`saveAsPdf.ts`)가 그 annotation 을 KaTeX 로 다시 렌더하므로, 매크로를 파서
 *    안에서만 풀면 화면은 멀쩡한 수식이 PDF 에만 깨져 나온다.
 * 2. `\mleft` 는 매크로로 확장하면 늦게 풀려 Temml 이 함수 뒤 fence 를 놓치고
 *    `\sin` 뒤에 0.1667em 을 넣는다(MathLive 의 mleft 는 tight spacing 변형이다).
 *
 * MathLive 밖(AI 결과 등)에서 온 LaTeX 도 이 렌더러를 지나므로, 매크로 이름과 같은
 * 글자를 건드리지 않는다. `\def\rd{x}` 를 치환하면 정의가 사라지는 데 그치지 않고
 * `\mathrm` 자체를 재정의해 수식 전체가 오염되고, `\verb|\rd|` 는 문자 그대로
 * 나와야 할 내용이 바뀐다. 정의·verb 구간만 정확히 건너뛰려면 파라미터·`\global`·
 * 그룹 스코프·verb 구분자까지 재현해야 하므로, 그 구문이 보이면 정규화를 통째로
 * 건너뛴다. MathLive 는 이들을 내보내지 않고, 섞인 입력은 기존처럼 Temml 실패 →
 * 폴백으로 떨어진다.
 */
export const normalizeMathliveLatex = (latex: string): string => {
  if (LITERAL_CONTEXT_PATTERN.test(latex)) return latex;

  return latex.replace(
    MATHLIVE_MACRO_PATTERN,
    (matched, backslashes: string, command: string) => {
      if (backslashes.length % 2 === 0) return matched;

      const replacement = MATHLIVE_MACROS[`\\${command}`];
      if (!replacement) return matched;

      return `${backslashes.slice(0, -1)}${replacement}`;
    },
  );
};
